import { FC, ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';
import { VisuallyHidden } from '@mantine/core';
import { MdArrowBack, MdChevronRight, MdLogout } from 'react-icons/md';
import type { IListElement } from '../../types/IListElement';
import AnchoredDialog from '../../molecules/AnchoredDialog';
import AppearanceSection from './AppearanceSection';
import UserMenuAccounts, { type IUserMenuAccountsProps } from './UserMenuAccounts';
import { UserMenuList, UserMenuSeparator } from './UserMenuParts';
import { userMenuItem } from './userMenuItem';
import './UserMenuDialog.scss';

/** Раздел меню профиля со своим экраном. */
export interface IUserMenuSection {
    /** Ключ: id экрана `rf-user-menu-screen-<id>`, кнопка раздела — `data-testid="user-menu-section-<id>"` */
    id: string;
    title: string;
    /** Строка под названием на кнопке раздела */
    hint?: string;
    /** Иконка 24px */
    icon: ReactNode;
    /** Пункты раздела: без пунктов раздела нет, единственный пункт стоит в корне меню */
    items?: IListElement[];
    /** Своё содержимое экрана (настройки, свои группы пунктов) — раздел есть всегда */
    content?: ReactNode;
}

export interface IUserMenuDialogProps {
    /** Якорь панели (`UserMenuButton` в обёртке, принимающей ref) */
    target: ReactNode;
    opened: boolean;
    onClose: () => void;
    /** Карточка пользователя (`UserPanel`) */
    profile?: ReactNode;
    /** Под карточкой: состояние замещений и т. п. */
    profileExtra?: ReactNode;
    /**
     * Учётные записи браузера (пропсы `UserMenuAccounts`, обычно из `useUserMenuAccounts`) — под карточкой, перед
     * первым разделителем; длинный список уходит на экран «Учётные записи»
     */
    accounts?: IUserMenuAccountsProps | null;
    /** Блок «Оформление»; по умолчанию — `AppearanceSection`, `null` — без него */
    appearance?: ReactNode;
    /** Свои блоки корня после «Оформления» (настройки приложения) */
    settings?: ReactNode;
    /** Разделы с вложенными экранами — после `settings` */
    sections?: IUserMenuSection[];
    /** «Выход из профиля»; без обработчика пункта нет */
    onLogout?: () => void;
    /** «Выйти из всех учётных записей» (мультиаккаунт) — под выходом из профиля */
    onLogoutAll?: () => void;
    /** Низ меню: версия приложения */
    footer?: ReactNode;
    /** Ширина панели @default 372 */
    width?: number;
}

const screenId = (section: string) => `rf-user-menu-screen-${section}`;

/** Экран всего списка учётных записей; id не пересекается с разделами приложения */
const ACCOUNTS_SCREEN = 'browser-accounts';

/** Раздел с экраном: свой контент или больше одного пункта; единственный пункт — в корне. */
const hasScreen = (section: IUserMenuSection) => !!section.content || (section.items?.length ?? 0) > 1;

/**
 * Меню профиля: одна панель у кнопки со стеком экранов вместо длинного плоского списка. Корень — то, что смотрят
 * каждый день (карточка, оформление, выход), разделы уходят на вложенные экраны внутри той же панели, без второго
 * поповера поверх первого.
 *
 * Экраны рендерятся все сразу, неактивные — с `hidden`: у кнопок разделов есть валидный `aria-controls`, скрытые
 * экраны не попадают ни в порядок Tab, ни в дерево доступности, а высоту панели задаёт видимый экран.
 */
const UserMenuDialog: FC<IUserMenuDialogProps> = ({
    target,
    opened,
    onClose,
    profile,
    profileExtra,
    accounts,
    appearance,
    settings,
    sections = [],
    onLogout,
    onLogoutAll,
    footer,
    width = 372,
}) => {
    const titleId = useId();
    const [screen, setScreen] = useState<string>('root');
    /** Направление последнего перехода — им CSS выбирает сторону въезда экрана. */
    const [direction, setDirection] = useState<'forward' | 'back'>('forward');
    const triggerRefs = useRef<Partial<Record<string, HTMLButtonElement | null>>>({});
    const backRefs = useRef<Partial<Record<string, HTMLButtonElement | null>>>({});
    /** Раздел, на кнопку которого вернуть фокус после «Назад». */
    const returnToRef = useRef<string | null>(null);

    // Закрытие сбрасывает состояние: следующее открытие всегда начинается с корня.
    useEffect(() => {
        if (!opened) {
            setScreen('root');
            setDirection('forward');
            returnToRef.current = null;
        }
    }, [opened]);

    // Фокус едет вместе с экраном: вглубь — на «Назад», обратно — на кнопку раздела.
    useEffect(() => {
        if (!opened) {
            return;
        }

        if (screen === 'root') {
            const section = returnToRef.current;

            if (section) {
                triggerRefs.current[section]?.focus();
                returnToRef.current = null;
            }

            return;
        }

        backRefs.current[screen]?.focus();
    }, [screen, opened]);

    const openSection = useCallback((section: string) => {
        returnToRef.current = section;
        setDirection('forward');
        setScreen(section);
    }, []);

    const goBack = useCallback(() => {
        setDirection('back');
        setScreen('root');
    }, []);

    const renderEntry = (section: IUserMenuSection) => {
        if (!hasScreen(section)) {
            return section.items?.length ? <UserMenuList key={section.id} items={section.items} /> : null;
        }

        return (
            <button
                key={section.id}
                type="button"
                ref={(node) => {
                    triggerRefs.current[section.id] = node;
                }}
                className="rf-user-menu-dialog__row"
                data-testid={`user-menu-section-${section.id}`}
                aria-haspopup="menu"
                aria-expanded={screen === section.id}
                aria-controls={screenId(section.id)}
                onClick={() => openSection(section.id)}
            >
                {section.icon}
                <span className="rf-user-menu-dialog__row-text">
                    {section.title}
                    {section.hint && <span className="rf-user-menu-dialog__row-hint">{section.hint}</span>}
                </span>
                <MdChevronRight size="20" className="rf-user-menu-dialog__row-chevron" aria-hidden="true" />
            </button>
        );
    };

    const renderScreen = (section: IUserMenuSection) => (
        <section
            key={section.id}
            id={screenId(section.id)}
            className="rf-user-menu-dialog__screen"
            data-screen={section.id}
            hidden={screen !== section.id}
        >
            <button
                type="button"
                ref={(node) => {
                    backRefs.current[section.id] = node;
                }}
                className="rf-user-menu-dialog__back"
                data-testid="user-menu-back"
                onClick={goBack}
            >
                <MdArrowBack size="20" aria-hidden="true" />
                <span>{section.title}</span>
            </button>
            {section.content ?? <UserMenuList items={section.items ?? []} />}
        </section>
    );

    return (
        <AnchoredDialog
            align="right"
            target={target}
            className="rf-user-menu-dialog"
            contentClassName="rf-user-menu-dialog__content"
            opened={opened}
            onClose={onClose}
            width={width}
            dropdownAria={{ 'aria-labelledby': titleId }}
        >
            <VisuallyHidden id={titleId}>Меню пользователя</VisuallyHidden>
            <div className="rf-user-menu-dialog__viewport" data-direction={direction} data-testid="user-menu-dialog">
                <section className="rf-user-menu-dialog__screen" data-screen="root" hidden={screen !== 'root'}>
                    {profile}
                    {profileExtra}
                    {/* На экране списка корневые строки не нужны: те же учётные записи дважды в DOM */}
                    {accounts && screen !== ACCOUNTS_SCREEN && (
                        <UserMenuAccounts
                            {...accounts}
                            onShowAll={() => openSection(ACCOUNTS_SCREEN)}
                            showAllRef={(node) => {
                                triggerRefs.current[ACCOUNTS_SCREEN] = node;
                            }}
                            showAllControls={screenId(ACCOUNTS_SCREEN)}
                        />
                    )}
                    <UserMenuSeparator />

                    {appearance !== null && (
                        <>
                            {appearance ?? <AppearanceSection />}
                            <UserMenuSeparator />
                        </>
                    )}

                    {settings}
                    {sections.map(renderEntry)}

                    {onLogout && (
                        <>
                            <UserMenuSeparator />
                            <UserMenuList
                                items={[
                                    userMenuItem({
                                        value: 'logout',
                                        label: 'Выход из профиля',
                                        icon: <MdLogout size="24" />,
                                        onSelect: onLogout,
                                        danger: true,
                                    }),
                                    ...(onLogoutAll
                                        ? [
                                              userMenuItem({
                                                  value: 'logout-all',
                                                  label: 'Выйти из всех учётных записей',
                                                  icon: <MdLogout size="24" />,
                                                  onSelect: onLogoutAll,
                                                  danger: true,
                                              }),
                                          ]
                                        : []),
                                ]}
                            />
                        </>
                    )}

                    {footer && (
                        <>
                            <UserMenuSeparator />
                            {footer}
                        </>
                    )}
                </section>

                {accounts && (
                    <section
                        id={screenId(ACCOUNTS_SCREEN)}
                        className="rf-user-menu-dialog__screen"
                        data-screen={ACCOUNTS_SCREEN}
                        hidden={screen !== ACCOUNTS_SCREEN}
                    >
                        <button
                            type="button"
                            ref={(node) => {
                                backRefs.current[ACCOUNTS_SCREEN] = node;
                            }}
                            className="rf-user-menu-dialog__back"
                            data-testid="user-menu-back"
                            onClick={goBack}
                        >
                            <MdArrowBack size="20" aria-hidden="true" />
                            <span>Учётные записи</span>
                        </button>
                        {screen === ACCOUNTS_SCREEN && <UserMenuAccounts {...accounts} screen />}
                    </section>
                )}
                {sections.filter(hasScreen).map(renderScreen)}
            </div>
        </AnchoredDialog>
    );
};

export default UserMenuDialog;
